import { CheckCircle2, XCircle, Clock, Phone, Mail } from "lucide-react";
import { eur, fmtDateTime } from "./format";
import { useT } from "./i18n";

function Contact({ company }) {
  if (!company?.phone && !company?.email) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-3 text-sm">
      {company.phone && (
        <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1.5 text-[#3F5222] font-semibold min-h-[40px]"><Phone size={14} /> {company.phone}</a>
      )}
      {company.email && (
        <a href={`mailto:${company.email}`} className="inline-flex items-center gap-1.5 text-[#3F5222] font-semibold min-h-[40px]"><Mail size={14} /> {company.email}</a>
      )}
    </div>
  );
}

export function FinalState({ data }) {
  const { state, company } = data;
  const { t, locale } = useT();
  if (state === "expired") {
    return (
      <div className="ds-card p-6 text-center" data-testid="quote-expired">
        <Clock size={36} className="mx-auto text-[#8A9098]" />
        <h2 className="font-display font-bold text-lg mt-3">{t("expired_title")}</h2>
        <p className="text-sm text-[#525252] mt-2">{t("expired_text")}</p>
        <div className="flex justify-center"><Contact company={company} /></div>
      </div>
    );
  }
  if (state === "rejected") {
    return (
      <div className="ds-card p-6 text-center" data-testid="quote-rejected">
        <XCircle size={36} className="mx-auto text-[#9B2C1F]" />
        <h2 className="font-display font-bold text-lg mt-3">{t("rejected_title")}</h2>
        <p className="text-sm text-[#525252] mt-2">{t("rejected_text", { fecha: fmtDateTime(data.rejected_at, locale) })}</p>
        <div className="flex justify-center"><Contact company={company} /></div>
      </div>
    );
  }
  const snap = data.accepted_snapshot || {};
  const addons = snap.addons || [];
  return (
    <div className="ds-card p-6" data-testid="quote-accepted">
      <div className="text-center">
        <CheckCircle2 size={36} className="mx-auto text-[#6E8B3D]" />
        <h2 className="font-display font-bold text-lg mt-3">{t("accepted_title")}</h2>
        <p className="text-sm text-[#525252] mt-1">{t("accepted_text", { fecha: fmtDateTime(data.accepted_at, locale) })}</p>
      </div>
      <div className="mt-5 space-y-1.5 text-sm">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{t("accepted_lines")}</div>
        {(snap.items || data.items || []).map((it, i) => (
          <div key={i} className="flex justify-between gap-3"><span>{it.name}</span><span className="font-mono">{eur(it.line_total)}</span></div>
        ))}
        {addons.length > 0 && (
          <>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] pt-2">{t("selected_extras")}</div>
            {addons.map((a) => (
              <div key={a.rule_id} className="flex justify-between gap-3" data-testid={`accepted-addon-${a.rule_id}`}><span>{a.part_name || a.title}</span><span className="font-mono">{eur(a.price)}</span></div>
            ))}
          </>
        )}
        {(snap.skipped || []).length > 0 && (
          <div className="mt-2 rounded-lg bg-[#FDEDE7] border border-[#E9A596] p-3" data-testid="accepted-skipped">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#9B2C1F]">{t("skipped_title")}</div>
            {snap.skipped.map((s) => (
              <div key={s.rule_id} className="text-xs text-[#9B2C1F] mt-1">{s.title} — {s.reason}. {t("skipped_text")}</div>
            ))}
          </div>
        )}
        <div className="flex justify-between items-baseline border-t border-[#E5E5E0] pt-2 mt-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{t("total_vat")}</span>
          <span className="font-display font-black text-xl" data-testid="accepted-grand-total">{eur(snap.grand_total ?? data.totals?.total)}</span>
        </div>
      </div>
      <Contact company={company} />
    </div>
  );
}
